import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getAuthToken } from '../services/authUtils';
import { wsService, WsEventType } from '../services/WebSocketService';
import { WS_URL } from '../config';

interface WebSocketContextType {
  /** True when WebSocket is connected and ready. */
  connected: boolean;
  /** Subscribe to a WebSocket event type. Returns unsubscribe function. */
  on: (eventType: WsEventType, callback: (data: any) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  connected: false,
  on: () => () => {},
});

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);

  // Connect when user signs in, disconnect on sign out
  useEffect(() => {
    if (!user || !WS_URL) return;

    let mounted = true;

    const connect = async () => {
      const token = await getAuthToken();
      if (token && mounted) {
        wsService.connect(token);
      }
    };

    connect();

    // Poll connection state (lightweight — just checks ws.readyState)
    const interval = setInterval(() => {
      if (mounted) setConnected(wsService.connected);
    }, 2000);

    return () => {
      mounted = false;
      clearInterval(interval);
      wsService.disconnect();
      setConnected(false);
    };
  }, [user]);

  const on = useCallback((eventType: WsEventType, callback: (data: any) => void) => {
    return wsService.on(eventType, callback);
  }, []);

  return <WebSocketContext.Provider value={{ connected, on }}>{children}</WebSocketContext.Provider>;
};
